import React, { useState, useEffect, useRef } from 'react';
import { Gift, Copy, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const ReferralButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [referralCode, setReferralCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [stats, setStats] = useState({ referral_count: 0 });
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (isOpen && !referralCode) {
      loadReferralCode();
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadReferralCode = async () => {
    setLoading(true);
    try {
      const res = await api.get('/referral/my-code');
      setReferralCode(res.data.code);
      setStats(res.data);
    } catch (err) {
      console.error('Error loading referral code:', err);
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    const url = `${window.location.origin}/r/${referralCode}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success('¡Link copiado!');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Button - Icon only */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`p-2 rounded-lg transition-all ${
          isOpen 
            ? 'bg-orange-500 text-white' 
            : 'bg-orange-500/10 hover:bg-orange-500/20 text-orange-400'
        }`}
        title="Invita amigos y gana 10%"
        data-testid="referral-button"
      >
        <Gift className="w-5 h-5" />
      </button>

      {/* Dropdown - Responsive positioning */}
      {isOpen && (
        <div className="fixed lg:absolute lg:bottom-full lg:left-0 lg:mb-2 top-14 lg:top-auto right-4 lg:right-auto w-72 bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl z-[100] overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Gift className="w-5 h-5 text-white" />
                <span className="text-white font-semibold">¡Invita y Gana!</span>
              </div>
              <button onClick={() => setIsOpen(false)} className="text-white/70 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 space-y-4">
            {/* Benefits */}
            <div className="flex gap-2 text-xs">
              <div className="flex-1 bg-green-500/10 border border-green-500/20 rounded-lg p-2 text-center">
                <span className="text-green-400 font-bold">10%</span>
                <p className="text-zinc-400">para ti</p>
              </div>
              <div className="flex-1 bg-blue-500/10 border border-blue-500/20 rounded-lg p-2 text-center">
                <span className="text-blue-400 font-bold">10%</span>
                <p className="text-zinc-400">para tu amigo</p>
              </div>
            </div>

            {/* Code */}
            {loading ? (
              <div className="flex justify-center py-2">
                <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : referralCode ? (
              <div className="space-y-2">
                <p className="text-zinc-500 text-xs">Tu link de referido:</p>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-zinc-800 rounded-lg px-3 py-2 truncate">
                    <code className="text-orange-400 text-sm">{`${window.location.origin.replace('https://', '')}/r/${referralCode}`}</code>
                  </div>
                  <button
                    onClick={copyLink}
                    className={`p-2 rounded-lg transition-colors flex-shrink-0 ${
                      copied 
                        ? 'bg-green-500/20 text-green-400' 
                        : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-400'
                    }`}
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
                
                {stats.referral_count > 0 && (
                  <p className="text-zinc-500 text-xs text-center pt-1">
                    {stats.referral_count} {stats.referral_count === 1 ? 'referido' : 'referidos'} ✨
                  </p>
                )}
              </div>
            ) : (
              <p className="text-zinc-500 text-sm text-center">Error al cargar</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferralButton;
